import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { useUser } from '@clerk/nextjs';
import { useDailyEvent, useMeetingState } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { ProviderCallContent } from '@web-app/components/CallV2/ProviderCallContent/ProviderCallContent';
import { WaitingRoomPopup } from '@web-app/components/CallV2/WaitingRoomPopup/WaitingRoomPopup';
import { CallQualitySurveyModal } from '@web-app/components/Daily/CallQualitySurveyModal/CallQualitySurveyModal';
import { DailyCallProvider } from '@web-app/components/Daily/DailyCallProvider/DailyCallProvider';
import {
  ProviderCallContextController,
  useProviderCallContext,
} from '@web-app/context/ProviderCallContext';
import {
  useVerifyRoom,
  VerifyRoomMutationError,
} from '@web-app/hooks/daily/verify-room';
import { useAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import { useMeetingToken } from '@web-app/hooks/queries/user/meetingToken';
import { useProviderVideoCallAnalytics } from '@web-app/hooks/useProviderVideoCallAnalytics';
import { useWaitingRoomLogging } from '@web-app/hooks/useWaitingRoomLogging';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { isBrowserWebdriver } from '@web-app/utils/is-browser-webdriver';
import { FC, useCallback, useEffect, useState } from 'react';

interface ProviderCallLayoutProps {
  userName?: string;
}

/**
 * Error screen component for call initialization failures
 */
const CallErrorScreen: FC<{
  error: string;
  details?: string;
  onRetry?: () => void;
  roomId?: string;
}> = ({ error, details, onRetry, roomId }) => {
  const handleRetry = useCallback(() => {
    if (onRetry) {
      datadogLogs.logger.info(
        'User clicked retry',
        callLogContext('call-join', {
          roomId: roomId ?? '',
          participantType: 'provider',
          errorType: error,
        }),
      );
      onRetry();
    }
  }, [onRetry, error, roomId]);

  return (
    <VStack
      mx="auto"
      h="100vh"
      p="2rem"
      flexDirection="column"
      justifyContent="center"
      textAlign="center"
      backgroundColor="#ECF3EE"
      spacing={6}
    >
      <Heading as="h1" size="2xl" color="status.error">
        Unable to Join Call
      </Heading>
      <Box maxW="600px">
        <Heading as="h2" size="lg" mb={4}>
          {error}
        </Heading>
        {details && (
          <Text fontSize="md" color="text.subtle" mb={4}>
            {details}
          </Text>
        )}
        <Text fontSize="sm" color="text.muted">
          If this error persists, please contact support.
        </Text>
      </Box>
      {onRetry && (
        <Button colorScheme="blue" size="lg" onClick={handleRetry}>
          Retry
        </Button>
      )}
    </VStack>
  );
};

/**
 * Inner component that handles survey logic.
 * Must be inside DailyProvider and ProviderCallContextController to access hooks.
 */
const ProviderCallWithSurvey: FC<{
  roomId: string;
  appointment: ReturnType<typeof useAppointmentByRoomId>['data'];
}> = ({ roomId, appointment }) => {
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [hasJoinedMeeting, setHasJoinedMeeting] = useState(false);
  const { duration } = useProviderCallContext();
  const meetingState = useMeetingState();
  const { user } = useUser();

  // Track video call analytics (provider joined/left)
  useProviderVideoCallAnalytics({
    roomId,
    appointmentId: appointment?.healthie_id,
    clientId: appointment?.client_id,
    providerId: appointment?.provider_id,
  });

  // Log waiting room events (patient requesting to join, admit, deny)
  useWaitingRoomLogging({
    roomId,
    healthieAppointmentId: appointment?.healthie_id,
    providerId: appointment?.provider_id,
    clientId: appointment?.client_id,
    userId: user?.id,
  });

  // Track when provider joins the meeting
  useEffect(() => {
    if (meetingState === 'joined-meeting') {
      setHasJoinedMeeting(true);
    }
  }, [meetingState]);

  // Listen for left-meeting event to show survey
  // Only show if provider was actually in a meeting (not on initial mount)
  // Disable survey in e2e tests to prevent flaky test failures from Daily.co spurious events
  const isE2ETest = isBrowserWebdriver();

  useDailyEvent(
    'left-meeting',
    useCallback(() => {
      if (!surveyCompleted && hasJoinedMeeting && !isE2ETest) {
        setShowSurvey(true);
      }
    }, [surveyCompleted, hasJoinedMeeting, isE2ETest]),
  );

  const handleSurveyClose = useCallback(() => {
    setSurveyCompleted(true);
    setShowSurvey(false);
  }, []);

  // Disable survey in e2e tests
  const shouldShowSurvey = showSurvey && !surveyCompleted && !isE2ETest;

  return (
    <>
      {shouldShowSurvey && (
        <CallQualitySurveyModal
          onSubmit={handleSurveyClose}
          onSkip={handleSurveyClose}
          dailyRoomId={roomId}
          healthieAppointmentId={appointment?.healthie_id}
          duration={duration.finalDuration}
          callExperienceVersion={2}
        />
      )}
      <WaitingRoomPopup />
      <ProviderCallContent roomId={roomId} appointment={appointment} />
    </>
  );
};

/**
 * Provider-facing call layout using Daily Prebuilt UI.
 * This is the outer wrapper that sets up DailyProvider context.
 * The actual hook logic is in ProviderCallContent (inner component).
 */
export const ProviderCallLayout: FC<ProviderCallLayoutProps> = ({
  userName,
}) => {
  const { roomId, roomUrl } = useDailyRoom();
  // roomId is guaranteed to exist: parent CallPageRootV2 guards with {!roomId ? <Loading/> : ...}
  const { data: appointment } = useAppointmentByRoomId(roomId);
  const { user } = useUser();
  const {
    data: meetingToken,
    isPending: isLoadingToken,
    error: tokenError,
    refetch: refetchToken,
  } = useMeetingToken(roomId as string, !!user);
  const {
    isPending: isVerifyingRoom,
    isRedirecting,
    error: verifyRoomError,
  } = useVerifyRoom(roomId, 'provider');

  // Loading state - show spinner while redirecting to new room, fetching token, or verifying room
  if (isRedirecting || isLoadingToken || isVerifyingRoom) {
    datadogLogs.logger.info(
      'Loading meeting token',
      callLogContext('call-join', {
        roomId: roomId as string,
        participantType: 'provider',
        hasUser: !!user,
        hasRoomUrl: !!roomUrl,
        hasUserName: !!userName,
        appointmentId: appointment?.healthie_id,
        userId: user?.id,
        isVerifyingRoom,
      }),
    );
    return <BerryLoader />;
  }

  // Error state - room verification returned a definitive error (ended/unavailable)
  if (verifyRoomError) {
    const error = verifyRoomError as VerifyRoomMutationError;
    if (error.reason === 'ended') {
      datadogLogs.logger.info(
        'Room verify: session has ended',
        callLogContext('room-verification', {
          roomId: roomId as string,
          participantType: 'provider',
        }),
      );
      return (
        <CallErrorScreen
          roomId={roomId}
          error="Session has ended"
          details="This appointment has already been marked as occurred. The video room is no longer available."
        />
      );
    }
    if (error.reason === 'unavailable') {
      datadogLogs.logger.info(
        'Room verify: appointment unavailable',
        callLogContext('room-verification', {
          roomId: roomId as string,
          participantType: 'provider',
          status: error.status,
        }),
      );
      return (
        <CallErrorScreen
          roomId={roomId}
          error="Appointment unavailable"
          details="This appointment is no longer available for a video call."
        />
      );
    }
    if (error.reason === 'not_found') {
      datadogLogs.logger.warn(
        'Room verify: appointment not found',
        callLogContext('room-verification', {
          roomId: roomId as string,
          participantType: 'provider',
        }),
      );
      return (
        <CallErrorScreen
          roomId={roomId}
          error="Appointment Not Found"
          details="This appointment is inactive and the meeting room is no longer available."
        />
      );
    }
    // Transient errors (network, timeout, etc.) - proceed anyway
    datadogLogs.logger.warn(
      'Room verify: transient error, proceeding',
      callLogContext('room-verification', {
        roomId: roomId as string,
        participantType: 'provider',
        error: error.message,
      }),
    );
  }

  // Error state - meeting token failed to load
  if (tokenError) {
    datadogLogs.logger.error(
      'Meeting token error',
      callLogContext('call-join', {
        roomId: roomId as string,
        participantType: 'provider',
        userId: user?.id,
        appointmentId: appointment?.healthie_id,
      }),
      tokenError instanceof Error ? tokenError : undefined,
    );
    return (
      <CallErrorScreen
        roomId={roomId}
        error="Failed to obtain meeting credentials"
        details="There was an error authenticating your session. This may be due to a temporary network issue or authentication problem."
        onRetry={() => refetchToken()}
      />
    );
  }

  // Error state - missing room URL
  if (!roomUrl) {
    datadogLogs.logger.error(
      'Missing room URL',
      callLogContext('call-join', {
        roomId: roomId as string,
        participantType: 'provider',
        userId: user?.id,
        appointmentId: appointment?.healthie_id,
        hasUserName: !!userName,
        hasMeetingToken: !!meetingToken,
      }),
    );
    return (
      <CallErrorScreen
        roomId={roomId}
        error="Invalid room configuration"
        details="The room URL could not be determined. Please check that you have the correct meeting link."
      />
    );
  }

  // Error state - missing username
  if (!userName) {
    datadogLogs.logger.error(
      'Missing username',
      callLogContext('call-join', {
        roomId: roomId as string,
        participantType: 'provider',
        userId: user?.id,
        appointmentId: appointment?.healthie_id,
        hasRoomUrl: !!roomUrl,
        hasMeetingToken: !!meetingToken,
      }),
    );

    return (
      <CallErrorScreen
        roomId={roomId}
        error="User information unavailable"
        details="Your user name could not be determined. Please try refreshing the page or logging in again."
      />
    );
  }

  // Error state - missing meeting token (but not loading and no error)
  if (!meetingToken?.token) {
    datadogLogs.logger.error(
      'Missing meeting token (no error thrown)',
      callLogContext('call-join', {
        roomId: roomId as string,
        participantType: 'provider',
        userId: user?.id,
        appointmentId: appointment?.healthie_id,
        hasRoomUrl: !!roomUrl,
        userName,
        meetingTokenData: meetingToken,
      }),
    );
    return (
      <CallErrorScreen
        roomId={roomId}
        error="Meeting token unavailable"
        details="Unable to generate a valid meeting token. This may indicate an issue with your account permissions. Please try logging out and back in."
        onRetry={() => refetchToken()}
      />
    );
  }

  // All required data is available - render the call interface
  datadogLogs.logger.info(
    'Successfully initialized call',
    callLogContext('call-join', {
      roomId: roomId as string,
      participantType: 'provider',
      userId: user?.id,
      userName,
      appointmentId: appointment?.healthie_id,
      clientId: appointment?.client_id,
      providerId: appointment?.provider_id,
      roomVerified: !verifyRoomError,
    }),
  );

  return (
    <DailyCallProvider
      roomUrl={roomUrl}
      userName={userName}
      token={meetingToken.token}
    >
      {/* Context provider for call state management */}
      <ProviderCallContextController
        roomId={roomId as string}
        appointment={appointment}
        isProvider={!!user}
      >
        {/* Inner component that handles survey and call content */}
        <ProviderCallWithSurvey
          roomId={roomId as string}
          appointment={appointment}
        />
      </ProviderCallContextController>
    </DailyCallProvider>
  );
};
